/**
 * O que a SERPRO vai responder na próxima interação deste inquilino (`R-05b`, `S5.2`).
 *
 * Só aparece em modo demonstração, e é isso que o torna honesto: numa instalação de
 * produção não existe botão que escolha o que o órgão de trânsito vai dizer. Ele estar
 * visível é, por si, um sinal de que aquela tela não é produção.
 *
 * O cenário mora **no banco e por inquilino** — não numa variável do processo. Dois
 * apresentadores na mesma instalação disputariam a mesma variável, e reiniciar o container
 * no meio da apresentação apagaria a preparação (risco nº 2 da §10 do cronograma).
 */
import { useCallback, useEffect, useState } from "react";

import { EVENTO_DO_CENARIO } from "../api/cenario";
import { ErroDaApi, api } from "../api/cliente";
import type { CenarioAtual } from "../api/tipos";

/**
 * O que cada cenário faz, na língua de quem apresenta. Os primeiros são o que a SENATRAN
 * emulada faz; os "Desfecho", o que a arrecadação da concessionária informa na consulta —
 * no contrato v3 quem decide a situação do pagamento é ela.
 *
 * **Tarefa 7:** "uma vez" é literal. Usado num envio (ou numa consulta, os de desfecho), o
 * seletor volta sozinho para "Registra a passagem". "Misto" é um modo e fica.
 *
 * **A contingência (`R-06`):** "Fora do ar (até voltar)" também fica — é a SENATRAN fora até
 * o apresentador escolher outro, e a faixa no topo não deixa ninguém esquecê-lo ligado.
 */
const ROTULO_DO_CENARIO: Record<string, string> = {
  ACEITO: "Registra a passagem",
  MISTO: "Misto: 1 em cada 3 é recusada",
  FORA_DO_AR: "Fora do ar (até voltar)",
  ERRO: "Recusa o registro (uma vez)",
  NAO_ENVIAVEL: "Recusa: fora das 24 h (uma vez)",
  OFFLINE: "Fora do ar (uma vez)",
  CANCELADO: "Cancela em 7 dias (uma vez)",
  PENDENTE: "Desfecho: pendente (uma vez)",
  PAGO: "Desfecho: pago (uma vez)",
  ISENTO: "Desfecho: isento (uma vez)",
};

/** A ordem do seletor: o normal, os modos que ficam, e depois os de uma vez. */
const ORDEM = Object.keys(ROTULO_DO_CENARIO);

export function SeletorDeCenario() {
  const [cenario, definirCenario] = useState<CenarioAtual | null>(null);

  useEffect(() => {
    let vivo = true;
    const ler = () =>
      void api<CenarioAtual>("/tollvision/cenario")
        .then((c) => vivo && definirCenario(c))
        // 402 aqui é o inquilino sem licença do TollVision. Não é erro a mostrar: é um
        // seletor que não faz sentido para ele, e some.
        .catch(() => vivo && definirCenario(null));
    ler();
    // Um cenário de uma vez pode ter sido usado por um envio ou uma consulta: relê.
    window.addEventListener(EVENTO_DO_CENARIO, ler);
    return () => {
      vivo = false;
      window.removeEventListener(EVENTO_DO_CENARIO, ler);
    };
  }, []);

  const escolher = useCallback(
    async (proximo: string) => {
      const anterior = cenario;
      // Otimista, mas com volta atrás: o seletor precisa responder ao clique, e um valor
      // que fica mostrando a escolha que o servidor recusou é pior do que um que pisca.
      definirCenario((c) =>
        c ? { ...c, proximo: proximo as CenarioAtual["proximo"] } : c,
      );
      try {
        await api<CenarioAtual>("/tollvision/cenario", {
          method: "PUT",
          corpo: { proximo, opcoes: [] },
        });
      } catch (falha) {
        definirCenario(anterior);
        if (!(falha instanceof ErroDaApi)) throw falha;
      }
    },
    [cenario],
  );

  if (!cenario) return null;

  return (
    <label className="flex flex-col leading-tight">
      <span className="text-rotulo tracking-[0.06em] text-azul-claro uppercase">
        Próxima resposta da SENATRAN
      </span>
      <select
        value={cenario.proximo}
        onChange={(e) => void escolher(e.target.value)}
        aria-label="Cenário da demonstração"
        className="rounded-botao border border-linha bg-painel-2 px-1.5 py-0.5 text-tinta-2"
      >
        {[...cenario.opcoes]
          .sort((a, b) => ORDEM.indexOf(a) - ORDEM.indexOf(b))
          .map((o) => (
            <option key={o} value={o}>
              {ROTULO_DO_CENARIO[o] ?? o}
            </option>
          ))}
      </select>
    </label>
  );
}
